import { Component, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
// Interfaces
import { AnalyticsDataSeries } from './../../models/analytics/analytics-data-series';
import { AnalyticsDataset } from './../../models/analytics/analytics-dataset';

@Component({
  selector: 'app-graph-row-global',
  templateUrl: './graph-row-global.component.html',
  styleUrls: ['./graph-row-global.component.less']
})
export class GraphRowGlobalComponent {

    public hoverDisplayValue: number;
    public hoverDisplayValueSecondary: number;
    public hoverDisplayLabel: string;
    public datasetEndValue: number;
    public datasetEndLabel: string;
    public secondaryDatasetEndValue: number;
    public secondaryDatasetEndLabel: string;
    public optionsObject: {};
    public _graphMin: number;
    public valueLabel: string;
    public valueLabelSecondary: string;
    private hoverIndex: number = -1;
    private _graphWidth: number;
    private chartData: AnalyticsDataSeries[];
    private chartLabels: string[];
    private chart: any;
    private chartElementCreated = false;

    // Chart Style Variables
    private highlightColour: string = '#178ae0';
    private highlightColourSecondary: string = '#8ea8bf';
    private backgroundColor: string = 'rgba(80, 172, 241, 0.2)';
    private borderColor: string = '#50acf1';
    private backgroundColorSecondary: string = 'rgba(142, 168, 191,  0.2)';
    private borderColorSecondary: string = '#8ea8bf';
    private pointBackgroundColor: string = 'rgba(0,0,0,0)';
    private pointBorderColor: string = 'rgba(0,0,0,0)';
    private pointHoverBackgroundColor: string = this.highlightColour;
    private pointHoverBorderColor: string = 'rgba(0,0,0,0)';
    private pointRadius: number = 8;
    private pointHoverRadius: number = 8;
    private barBackgroundColor: string = '#e2eaf1';

    @ViewChild('chart') chartDiv;
    @Input() size: string;
    @Input() calcStepSize: number;
    @Input() valueSuffix: string = '';
    @Input() graphKind: string;
    @Input() graphUseContext: string = 'separateGraph';
    @Input()
    set graphWidth(graphWidth: number) {
        if (this.chart) {
            this.chart.resize();
        }
        this._graphWidth = graphWidth;
    };

    @Input()
    set graphMin(graphMin: number) {
        this._graphMin = graphMin;
    }

    @Input()
    set globalHoverState(hoverIndex: number) {
        this.hoverIndex = hoverIndex;
        if (Number.isInteger(hoverIndex)) {this.setLocalHoverState(hoverIndex); };
    }

    @Input()
    set chartDataset(dataset: AnalyticsDataset) {
        this.chartData = dataset.barChartData;
        this.chartLabels = dataset.barChartLabels;
        let canvas = document.createElement('canvas');
        canvas.height = (this.size === 'main' ? 300 : 150);
        if (this._graphWidth) {canvas.width = this._graphWidth; };
        canvas.addEventListener('mouseout', (event) => {this.chartMouseout(event); }, false);
        if (this.chartElementCreated) {
            while (this.chartDiv.nativeElement.firstChild) {
                this.chartDiv.nativeElement.removeChild(this.chartDiv.nativeElement.firstChild);
            }
        };
        let canvasElement: any = this.chartDiv.nativeElement.appendChild(canvas);
        let ctx = canvasElement.getContext('2d');
        this.chartElementCreated = true;

        let chartObject: any = {
            type: this.graphKind === 'bar' ? 'bar' : 'line',
            options: this.recreateOptionsObject(),
            data: {
                labels: dataset.barChartLabels,
                datasets: dataset.barChartData.map((series, index) => ({
                    label: series.unit,
                    data: series.data,
                    backgroundColor: index === 0 ? this.backgroundColor : this.backgroundColorSecondary,
                    borderColor: index === 0 ? this.borderColor : this.borderColorSecondary
                })),
            }
        };


        if (this.graphKind.startsWith('line')) {
            chartObject.data.datasets.forEach((datasetObj, index) => {
                datasetObj.pointBackgroundColor = this.pointBackgroundColor;
                datasetObj.pointBorderColor = this.pointBorderColor;
                datasetObj.pointHoverBackgroundColor = index === 0 ? this.pointHoverBackgroundColor : this.highlightColourSecondary;
                datasetObj.pointHoverBorderColor = this.pointHoverBorderColor;
                datasetObj.pointRadius = this.pointRadius;
                datasetObj.pointHoverRadius = this.pointHoverRadius;
                datasetObj.cubicInterpolationMode = 'monotone';
            });
        }

        this.chart = new Chart(ctx, chartObject);

        if(dataset.total) {
            this.datasetEndLabel = 'Total';
            this.datasetEndValue = dataset.total;
        }
        else {
            this.datasetEndValue = dataset.barChartData[0].data[dataset.barChartData[0].data.length - 1];
            this.datasetEndLabel = dataset.barChartLabels[dataset.barChartLabels.length - 1];
        }

        if(dataset.secondaryTotal) {
            this.secondaryDatasetEndLabel = 'Total';
            this.secondaryDatasetEndValue = dataset.secondaryTotal;
        }
        else if(dataset.barChartData.length > 1) {
            this.secondaryDatasetEndValue = dataset.barChartData[1].data[dataset.barChartData[1].data.length - 1];
            this.secondaryDatasetEndLabel = dataset.barChartLabels[dataset.barChartLabels.length - 1];
        }
        //
        this.setLocalHoverState(-1);
    }

    @Output() setGlobalHoverState: EventEmitter<number> = new EventEmitter<number>();

    constructor(private elementRef: ElementRef) {}

    setLocalHoverState(hoverIndex: number) {
        if (this.chartData) {
            this.setHoverDisplays(hoverIndex);
            this.setHoverHighlight(hoverIndex);
        }
    }

    recreateOptionsObject() {
        let optionsObject: any = {
            responsive: false,
            maintainAspectRatio: false,
            animation: {
                duration: 50
            },
            plugins: [{
                afterInit: () => {this.setHoverHighlight(this.hoverIndex); }
            }],
            layout: {
                padding: {
                    left: 0,
                    right: 0,
                    top: 0,
                },
                margin: {
                    left: 0,
                    top: 0,
                }
            },
            legend: {
                display: false
            },
            hover: {
                mode: 'index',
                intersect: false,
                onHover: (active: Array<any>) => {
                    if (active && !active.length) {
                    return;
                    }
                    this.chartHovered({active});
                }
            },
            tooltips: {
                mode: 'label',
                enabled: false
            },
            scales: {
                xAxes: [{
                    position: 'bottom',
                    display: false
                }],
                yAxes: [{
                    display: false,
                    ticks: {
                    beginAtZero: true
                    }
                }]
            }
        };
        if (this.graphKind.startsWith('line')) {
            optionsObject.layout.padding.top = 8;
            optionsObject.layout.padding.right = 8;
        };
        if (!this._graphWidth) {optionsObject.responsive = true; };
        return optionsObject;
    }

    // private interpolationFunction(unixDate: number) {

    // }

    // private scaleXToFx(x: number) {

    // }

    // private scaleFxToX(Fx: number) {
    // }

    // private scaleYToFy(y: number) {
    // }

    // private scaleFyToY(y: number) {
    // }

    public chartHovered(e: any): void {
        if (this.graphUseContext === 'linkedGraph') {
            this.setGlobalHoverState.emit(e.active[0]._index);
        } else if (this.graphUseContext === 'separateGraph') {
            this.setLocalHoverState(e.active[0]._index)
        }
    }

    public chartMouseout(e: any): void {
        if (this.graphUseContext === 'linkedGraph') {
            this.setGlobalHoverState.emit(-1);
        } else if (this.graphUseContext === 'separateGraph') {
            this.setLocalHoverState(-1);
        }
    }

    private setHoverHighlight(hoverIndex) {
        if (this.chart) {
            if (this.graphKind === 'bar') {
                if (hoverIndex === -1) {
                    // TODO: This needs to change to handle cumulative default/mouseout hover display totals
                    if (this.datasetEndLabel === 'Total') {
                        this.chart.data.datasets[0].backgroundColor = Array(this.chartData[0].data.length).fill(this.highlightColour);
                    } else {
                        this.chart.data.datasets[0].backgroundColor = Array(this.chartData[0].data.length).fill(this.barBackgroundColor);
                        this.chart.data.datasets[0].backgroundColor[this.chartData[0].data.length - 1] = this.highlightColour;
                    }
                } else {
                    this.chart.data.datasets[0].backgroundColor = Array(this.chartData[0].data.length).fill(this.barBackgroundColor);
                    this.chart.data.datasets[0].backgroundColor[hoverIndex] = this.highlightColour;
                }
            } else if (this.graphKind.startsWith('line')) {
                this.chart.data.datasets[0].pointBackgroundColor = Array(this.chartData[0].data.length).fill(this.pointBackgroundColor);
                if (hoverIndex === -1) {
                    // TODO: This needs to change to handle cumulative default/mouseout hover display totals
                    this.chart.data.datasets[0].pointBackgroundColor[this.chartData[0].data.length - 1] = this.highlightColour;
                } else {
                    this.chart.data.datasets[0].pointBackgroundColor[hoverIndex] = this.highlightColour;
                }

                if (this.chart.data.datasets.length > 1) {
                    this.chart.data.datasets[1].pointBackgroundColor = Array(this.chartData[1].data.length).fill(this.pointBackgroundColor);
                    if (hoverIndex === -1) {
                        this.chart.data.datasets[1].pointBackgroundColor[this.chartData[1].data.length - 1] = this.highlightColourSecondary;
                    }
                    else {
                        this.chart.data.datasets[1].pointBackgroundColor[hoverIndex] = this.highlightColourSecondary;
                    }
                }

            }
            this.chart.update();
        }
    }

    private setHoverDisplays(hoverIndex: number) {
        this.setSeriesHoverDisplay(hoverIndex, 0);
        if (this.chartData.length > 1) {
            this.setSeriesHoverDisplay(hoverIndex, 1);
        }
    }

    private setSeriesHoverDisplay(hoverIndex: number, seriesIndex: number) {
        let value: number;
        let date: string;
        if (hoverIndex === -1) {
            value = seriesIndex === 0 ? this.datasetEndValue : this.secondaryDatasetEndValue;
            date = seriesIndex === 0 ? this.datasetEndLabel : this.secondaryDatasetEndLabel;
        } else {
            value = this.chartData[seriesIndex].data[hoverIndex];
            date = this.chartLabels[hoverIndex];
        }

        let valueLabelProp = seriesIndex === 0 ? 'valueLabel' : 'valueLabelSecondary';
        let valueProp = seriesIndex === 0 ? 'hoverDisplayValue' : 'hoverDisplayValueSecondary';

        let dataset = this.chartData[seriesIndex];
        if(dataset.unit === 'TIME') {
            if(value < 240 * 60) {
                this[valueLabelProp] = 'Minutes';
                // display to nearest minute
                this[valueProp] = Math.round(value / 60);
            }
            else if(value < 168 * 3600) {
                this[valueLabelProp] = 'Hours';
                // display to nearest hour
                this[valueProp] = Math.round(value / 3600);
            }
            else {
                this[valueLabelProp] = 'Days';
                // display to nearest day
                this[valueProp] = Math.round(value / 86400);
            }
        }
        else {
            this[valueLabelProp] = dataset.unit;
            this[valueProp] = value;
        }
        this.hoverDisplayLabel = date;
    }
}



// WEBPACK FOOTER //
// /home/runner/work/nomadic-admin/nomadic-admin/nomadic-admin/src/app/shared/graph-row-global/graph-row-global.component.ts